import React, { useEffect, useMemo, useState } from 'react';
import { OnChange } from 'react-final-form-listeners';
import { useFormState, useForm } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { createFilterOptions } from '@material-ui/lab/useAutocomplete';

import QCXAutocompleteSelect from '../../autocomplete-select/QCXAutocompleteSelect'; // lista suspensa

export default function DominioDinamico({
  metadado,
  isConsult,
  codAtributoPai,
  basePath = '',
  combinacoesValidas,
  ...restProps
}) {
  const { values } = useFormState();
  const form = useForm();

  const [label, setLabel] = useState('');
  const [name, setName] = useState('');
  const [codigoAtributo, setCodigoAtributo] = useState('');
  const [defaultValue, setDefaultValue] = useState('');

  const filterOptions = createFilterOptions({
    stringify: (item) => `${item.id} - ${item.value}`,
  });

  /*
        filtro condicional de atributos, a API do sisco retorna as possibilidades de combinações possiveis de atributos,
        trabalhar em cima dessa estrutura de dados para exibir os campos para o usuario ficou com um pouco complexo. No
        futuro para simplificar o codigo, seria interessante implementar uma arvore de decisão para tratar essas possibilidades
        como não teria um nó raiz, recomendo criar um "dicionario", que representaria cada nó da arvore e o seu endereço dentro da
        estrutura de dados, dessa forma, cada nó pode ser acessado independentemente como se fosse um nó raiz, apesar do conceito
        parecer complexo a implementação ficaria bem mais amigavel do que o código atual.

        ex de caso para futura melhoria:

        {
            "tributo": {
                "codigo": "6",
                "nome": "PIS IMPORTAÇÃO"
            },
            "regime": {
                "codigo": "1",
                "nome": "RECOLHIMENTO INTEGRAL"
            },
            "fundamentoLegal": {
                "codigo": "1100",
                "nome": "PIS-IMPORTAÇÃO e COFINS-IMPORTAÇÃO",
                "tipo": "Normal"
            },
            "mercadorias": [
                {
                    "atributos": [
                        {
                            "codigo": "ATT_4562",
                            "descricaoCodigo": "Uso autopeças",
                            "tipoCodigo": "Domínio dinâmico",
                            "valor": "0002",
                            "descricaoValor": "Importador NÃO é pessoa jurídica fabricante de máquinas e veículos relacionados no Anexo II do Art. 1º da Lei 10.485/2002. (§9º, Art. 8º. Lei 10865/2004)\n"
                        },
                        {
                            "codigo": "ATT_4540",
                            "descricaoCodigo": "Anexo II da Lei 10.485/2002",
                            "tipoCodigo": "Domínio dinâmico",
                            "valor": "0001",
                            "descricaoValor": "Se enquadra na descrição do Anexo II da Lei 10.485/2002"
                        }
                    ]
                },
                {
                    "atributos": [
                        {
                            "codigo": "ATT_4562",
                            "descricaoCodigo": "Uso autopeças",
                            "tipoCodigo": "Domínio dinâmico",
                            "valor": "0002",
                            "descricaoValor": "Importador NÃO é pessoa jurídica fabricante de máquinas e veículos relacionados no Anexo II do Art. 1º da Lei 10.485/2002. (§9º, Art. 8º. Lei 10865/2004)\n"
                        },
                        {
                            "codigo": "ATT_4540",
                            "descricaoCodigo": "Anexo II da Lei 10.485/2002",
                            "tipoCodigo": "Domínio dinâmico",
                            "valor": "XXXX",
                            "descricaoValor": "Não se enquadra em outra opção"
                        }
                    ]
                },
                {
                    "atributos": [
                        {
                            "codigo": "ATT_4562",
                            "descricaoCodigo": "Uso autopeças",
                            "tipoCodigo": "Domínio dinâmico",
                            "valor": "XXXX",
                            "descricaoValor": "Não se enquadra em outra opção"
                        }
                    ]
                }
            ]
        }


        O codigo abaixo faz o filtro iterando todas as combinações diversas vezes, o que não é muito performático.

    */

  const opcoesDisponiveis = useMemo(() => {
    if (!metadado?.dominio) return [];

    const { codTratamento } = metadado;
    const combinacoes = combinacoesValidas?.[codTratamento] || [];

    if (!combinacoes.length) {
      return metadado.dominio;
    }

    // valores de metadados, incluindo as infos que o usuário já preencheu
    const valoresFormulario = values?.ignorableFields?.tratamentosTtce?.metadados?.valores || {};

    // extrair outros atributos que o usuário já PREENCHEU, exceto o atual
    const outrosAtributosSelecionados = Object.entries(valoresFormulario)
      .filter(([codigo, valorObj]) => {
        const incluir =
          codigo !== metadado.codigo && valorObj?.valor && valorObj?.valor !== '' && valorObj?.valor !== null;

        return incluir;
      })
      .map(([codigo, obj]) => ({
        codigo,
        valor: obj.valor,
      }));

    // se o usuario nao preencheu nada, nao tem motivos para realizar filtro condicional.
    if (outrosAtributosSelecionados.length === 0) {
      return metadado.dominio;
    }

    // filtra outros atributos que está dentro de alguma combinação válida
    const combinacoesCompativeis = combinacoes.filter((combinacao) => {
      // Para CADA combinação possível da api
      const compativel = outrosAtributosSelecionados.every(({ codigo, valor }) => {
        const attrNaCombinacao = combinacao.find((a) => a.codigo === codigo);
        const ehCompativel = !attrNaCombinacao || attrNaCombinacao.valor === valor || attrNaCombinacao.valor === ""; // se nao tem combinação entao o campo é independente, ou tem combinação e o valor é valido

        return ehCompativel;
      });

      return compativel;
    });

    if (combinacoesCompativeis.length === 0) {
      return metadado.dominio;
    }

    // extrair valores possíveis para o atributo atual
    const valoresPossiveis = [];
    let atributoNaoAplicavel = false;

    // combinacoesCompativeis -> combinações que são válidas considerando o que o usuário já selecionou nos outros campos
    // obtem as opções compativeis para o atributo atual, considerando as seleções dos outros atributos.
    combinacoesCompativeis.forEach((combinacao) => {
      const attr = combinacao.find((a) => a.codigo === metadado.codigo);

      if (attr) {
        valoresPossiveis.push(attr.valor);
      } else {
        atributoNaoAplicavel = true;
      }
    });

    // se TODAS as combinações compatíveis não têm esse atributo, significa que ele não deve ser preenchido
    if (atributoNaoAplicavel && valoresPossiveis.length === 0) {
      return [];
    }

    // remover duplicatas
    const valoresUnicos = [...new Set(valoresPossiveis)];

    // filtrar o domínio para manter só os valores válidos
    const opcoesFiltradas = metadado.dominio.filter((opcao) => {
      const incluir = valoresUnicos.includes(opcao.valor);
      return incluir;
    });

    // Se não sobrou nenhuma opção, retornar todas
    return opcoesFiltradas.length > 0 ? opcoesFiltradas : metadado.dominio;
  }, [metadado, combinacoesValidas, values]);

  const listValores = useMemo(
    () =>
      opcoesDisponiveis.map((el) => ({
        id: el.valor,
        value: el.descricao,
      })),
    [opcoesDisponiveis]
  );

  useEffect(() => {
    if (!name || !metadado?.codigo) return;

    const valorAtual = values?.ignorableFields?.tratamentosTtce?.metadados?.valores?.[metadado.codigo]?.valor;

    if (valorAtual) {
      const valorAindaValido = opcoesDisponiveis.some((opcao) => opcao.valor === valorAtual);

      // se o valor atual não está mais nas opções disponíveis, limpar
      if (!valorAindaValido) {
        form.change(name, null);
      }
    }
  }, [opcoesDisponiveis, name, metadado?.codigo, form, values]);

  useEffect(() => {
    if (!metadado) return;

    setCodigoAtributo(metadado.codigo || '');
    setLabel(`${metadado.descricaoCodigo} (${metadado.codigo})` || '');
    setName(`${basePath}.${metadado.codigo}.valor` || '');
    setDefaultValue({ id: null, value: '' });
  }, [metadado, basePath]);

  if (!name) {
    return null;
  }

  if (listValores.length === 0) {
    return null;
  }

  return (
    <div style={{ display: listValores.length > 0 ? 'block' : 'none' }}>
      <QCXAutocompleteSelect
        label={label}
        id={name}
        name={name}
        options={listValores}
        filterOptions={filterOptions}
        defaultValue={defaultValue}
        getOptionLabel={(item) => (item.value ? `${item.id} - ${item.value}` : '')}
        required
        isConsult={isConsult}
        disabled={isConsult}
        {...restProps}
      />
    </div>
  );
}
